import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import Icon from '../../../components/AppIcon';

const UsageTrendChart = ({ data, selectedMethods, onMethodToggle }) => {
  const [hiddenMethods, setHiddenMethods] = useState([]);

  const methodColors = {
    'VA Transfer': '#3B82F6',
    'QRIS': '#A855F7',
    'OVO': '#6366F1',
    'DANA': '#06B6D4',
    'GoPay': '#10B981',
    'Card': '#F97316'
  };

  const handleLegendClick = (methodName) => {
    setHiddenMethods((prev) =>
      prev?.includes(methodName)
        ? prev?.filter((m) => m !== methodName)
        : [...prev, methodName]
    );
  };

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000)?.toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000)?.toFixed(0)}K`;
    return value;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;

    return (
      <div className="bg-popover border border-border rounded-lg shadow-elevation-3 p-3 md:p-4">
        <p className="text-xs md:text-sm font-semibold text-foreground mb-2 md:mb-3">
          {label}
        </p>
        <div className="space-y-1.5 md:space-y-2">
          {payload?.map((entry) => (
            <div key={entry?.name} className="flex items-center justify-between gap-4 md:gap-6">
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-xs md:text-sm text-muted-foreground">
                  {entry?.name}
                </span>
              </div>
              <span className="text-xs md:text-sm font-semibold text-foreground">
                {entry?.value?.toLocaleString('id-ID')}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 mb-4 md:mb-6">
        <div>
          <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            Payment Method Usage Trends
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground mt-1 md:mt-2">
            Transaction volume over time by payment method
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Info" size={16} className="text-muted-foreground" />
          <span className="text-xs md:text-sm text-muted-foreground">
            Click legend to toggle
          </span>
        </div>
      </div>
      <div className="h-64 md:h-80 lg:h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              {selectedMethods?.map((method) => (
                <linearGradient
                  key={method}
                  id={`color${method?.replace(/\s+/g, '')}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={methodColors?.[method]}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={methodColors?.[method]}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              onClick={(e) => handleLegendClick(e?.value)}
              wrapperStyle={{ cursor: 'pointer', paddingTop: '20px' }}
            />
            {selectedMethods?.map((method) => (
              <Area
                key={method}
                type="monotone"
                dataKey={method}
                stroke={methodColors?.[method]}
                fillOpacity={1}
                fill={`url(#color${method?.replace(/\s+/g, '')})`}
                strokeWidth={2}
                hide={hiddenMethods?.includes(method)}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex flex-wrap gap-2 md:gap-3">
          {selectedMethods?.map((method) => (
            <button
              key={method}
              onClick={() => handleLegendClick(method)}
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-250 ${
                hiddenMethods?.includes(method)
                  ? 'bg-muted text-muted-foreground'
                  : 'text-white'
              }`}
              style={{
                backgroundColor: hiddenMethods?.includes(method)
                  ? undefined
                  : methodColors?.[method]
              }}
            >
              {method}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsageTrendChart;